import React, { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import cidLogo from '../../assets/cid_logo.png';
import {
  LayoutDashboard,
  FolderOpen,
  Upload,
  Save,
  Key,
  CheckCircle,
  Bell,
  Archive as ArchiveIcon,
  Star,
  Clock,
  ScrollText,
  FolderTree,
  FolderSync,
  Settings,
  Search,
  FileBarChart,
  X,
} from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { useUIStore } from '../../store/uiStore';
import { useNotificationStore } from '../../store/notificationStore';
import { usePortalAccess } from '../../hooks/usePortalAccess';
import { ROUTES } from '../../config/routes.config';

const baseClasses =
  'flex items-center gap-3 px-3 py-2 rounded-md text-md font-medium transition-colors';

const Sidebar: React.FC = () => {
  const {
    canView,
    canUpload,
    canApprove,
    canManageContent,
    canAccessRequest,
    canViewReports,
    isAdmin,
  } = usePortalAccess();
  const { sidebarOpen, mobileSidebarOpen, setMobileSidebarOpen } = useUIStore();
  const { unreadCount } = useNotificationStore();
  const location = useLocation();

  const closeMobile = useCallback(() => setMobileSidebarOpen(false), [setMobileSidebarOpen]);

  useEffect(() => {
    closeMobile();
  }, [location.pathname, closeMobile]);

  const items = [
    ...(canView ? [{ icon: LayoutDashboard, label: 'Dashboard', path: ROUTES.DASHBOARD }] : []),
    ...(canView ? [{ icon: FolderOpen, label: 'Cases', path: ROUTES.CASES }] : []),
    ...(canView ? [{ icon: Search, label: 'Search Documents', path: ROUTES.SEARCH }] : []),
    ...(canUpload ? [{ icon: Upload, label: 'Upload Documents', path: ROUTES.UPLOAD }] : []),
    ...(canUpload ? [{ icon: Save, label: 'Drafts', path: ROUTES.DRAFTS }] : []),
    ...(canApprove ? [{ icon: CheckCircle, label: 'Upload Status', path: ROUTES.UPLOAD_STATUS }] : []),
    ...(canAccessRequest
      ? [
          { icon: Key, label: 'Access Request', path: ROUTES.ACCESS_REQUEST },
          { icon: CheckCircle, label: 'Access Status', path: ROUTES.ACCESS_STATUS },
        ]
      : []),
    { icon: Bell, label: 'Notifications', path: ROUTES.NOTIFICATIONS },
    ...(canManageContent
      ? [{ icon: FolderTree, label: 'Content Mgmt', path: ROUTES.CONTENT_MGMT }]
      : []),
    ...(canView ? [{ icon: ArchiveIcon, label: 'Archive', path: ROUTES.ARCHIVE }] : []),
    ...(canView ? [{ icon: Star, label: 'Favorites', path: ROUTES.FAVORITES }] : []),
    ...(canView ? [{ icon: Clock, label: 'Latest Files', path: ROUTES.LATEST_FILES }] : []),
    ...(canView ? [{ icon: ScrollText, label: 'Audit Log', path: ROUTES.AUDIT }] : []),
    ...(canViewReports ? [{ icon: FileBarChart, label: 'Reports', path: ROUTES.REPORTS }] : []),
    ...(isAdmin
      ? [
          { icon: FolderSync, label: 'Storage Sync', path: ROUTES.STORAGE_SYNC },
          { icon: Settings, label: 'Admin Panel', path: ROUTES.ADMIN },
        ]
      : []),
  ];

  const sidebarContent = (
    <>
      <div className="flex items-center gap-3 px-4 py-4 border-b border-white/10">
        <img
          src={cidLogo}
          alt="CID Logo"
          style={{
            height: 36,
            width: 36,
            objectFit: 'contain',
            flexShrink: 0,
            borderRadius: '50%',
            background: 'white',
            padding: '3px',
          }}
        />
        <div>
          <div className="text-md font-semibold tracking-wide">RRMS</div>
          <div className="text-sm text-white/70">
            Records Room Management
          </div>
        </div>
      </div>
      <nav className="flex-1 px-2 py-4 pr-1 space-y-1 overflow-y-auto sidebar-scroll">
        {items.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              [
                baseClasses,
                isActive
                  ? 'bg-[var(--bg-sidebar-active)] text-white'
                  : 'text-white/80 hover:bg-[var(--bg-sidebar-hover)]',
              ].join(' ')
            }
          >
            <item.icon className="h-4 w-4" />
            {(sidebarOpen || mobileSidebarOpen) && (
              <>
                <span>{item.label}</span>
                {item.path === ROUTES.NOTIFICATIONS && unreadCount > 0 && (
                  <span
                    style={{
                      marginLeft: 'auto',
                      minWidth: 18,
                      height: 18,
                      borderRadius: 'var(--radius-full)',
                      background: 'var(--color-error)',
                      color: 'white',
                      fontSize: '10px',
                      fontWeight: 'var(--font-weight-bold)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 4px',
                    }}
                  >
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className="hidden md:flex flex-col text-[var(--color-gray-100)]"
        style={{
          width: sidebarOpen
            ? 'var(--sidebar-width)'
            : 'var(--sidebar-collapsed-width)',
          height: '100vh',
          backgroundColor: 'var(--bg-sidebar)',
          flexShrink: 0,
          transition: 'width 0.2s ease',
        }}
      >
        {sidebarContent}
      </aside>

      {/* Mobile backdrop */}
      {mobileSidebarOpen && (
        <div
          role="presentation"
          onClick={closeMobile}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 998,
          }}
          className="md:hidden"
        />
      )}

      {/* Mobile sidebar overlay */}
      <aside
        className="md:hidden flex flex-col text-[var(--color-gray-100)]"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: 'var(--sidebar-width)',
          height: '100vh',
          backgroundColor: 'var(--bg-sidebar)',
          zIndex: 999,
          transform: mobileSidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.25s ease',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 8px 0 0' }}>
          <button
            type="button"
            onClick={closeMobile}
            aria-label="Close sidebar"
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-gray-300)',
              cursor: 'pointer',
              padding: 4,
            }}
          >
            <X size={20} />
          </button>
        </div>
        {sidebarContent}
      </aside>
    </>
  );
};

export default Sidebar;
